import type { MouseEvent } from 'react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/contexts/AuthContext'
import { useLoginAlert } from '@/contexts/LoginAlertContext'
import { useAddPurchase, usePurchases } from '@/hooks/usePurchases'
import { BagCheckIcon, BagIcon, BagPlusIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/Spinner'

interface AddPurchaseButtonProps {
  productId: string
  productTitle: string
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Botão da sacola no canto do card.
 *
 * Cinza em repouso, verde no hover do card (a classe `group-hover` vem do card
 * que envolve o botão) e verde com "check" quando o produto já está na lista.
 * Sem sessão, dispara a tarja de aviso em vez de chamar a API.
 */
export function AddPurchaseButton({
  productId,
  productTitle,
  size = 'md',
  className,
}: AddPurchaseButtonProps) {
  const { isAuthenticated } = useAuth()
  const { show: showLoginAlert } = useLoginAlert()
  const { purchasedProductIds } = usePurchases()
  const addPurchase = useAddPurchase()

  const alreadyBought = purchasedProductIds.has(productId)
  const isPending = addPurchase.isPending && addPurchase.variables?.productId === productId
  const box = size === 'sm' ? 'size-8' : 'size-9'
  const glyph = size === 'sm' ? 'size-4' : 'size-[18px]'

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    // O botão vive dentro de um <Link>: impede a navegação para o produto.
    event.preventDefault()
    event.stopPropagation()

    if (!isAuthenticated) {
      showLoginAlert()
      return
    }
    if (alreadyBought || isPending) return
    addPurchase.mutate({ productId })
  }

  const label = alreadyBought
    ? `${productTitle} já está na sua lista de comprados`
    : `Marcar ${productTitle} como comprado`

  return (
    <button
      type="button"
      onClick={handleClick}
      title={alreadyBought ? 'Já está na sua lista' : 'Marcar como comprado'}
      aria-label={label}
      aria-pressed={alreadyBought}
      className={cn(
        'inline-flex items-center justify-center rounded-full transition-colors',
        box,
        alreadyBought
          ? 'bg-brand text-white'
          : 'bg-black/10 text-white group-hover:bg-brand group-hover:text-white hover:!bg-brand-dark',
        className,
      )}
    >
      {isPending ? (
        <Spinner size={16} label={null} />
      ) : alreadyBought ? (
        <BagCheckIcon className={glyph} />
      ) : (
        <>
          <BagIcon className={cn(glyph, 'group-hover:hidden')} />
          <BagPlusIcon className={cn(glyph, 'hidden group-hover:block')} />
        </>
      )}
    </button>
  )
}
